import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { IS_MAC } from '../lib/shortcut';
import { BackspaceIcon, CommandIcon, EnterIcon, type TIconProps } from './icons';

export type TKeycapTone = 'on-accent' | 'on-plate' | 'muted' | 'menu';

export interface IKey {
  id: string;
  Glyph?: (props: TIconProps) => ReactNode;
  label?: string;
}

// `muted`: a bright plate on a disabled button reads as the only live thing on it.
const TONE: Record<TKeycapTone, string> = {
  'on-accent': 'bg-surface text-accent',
  'on-plate': 'bg-surface text-muted',
  muted: 'bg-surface-sunken text-faint',
  menu: 'border border-border-raised bg-surface text-muted',
};

// A PC keyboard has no ⌘ on it, so that platform gets the word it is printed with.
export const SUBMIT_KEYS: readonly IKey[] = [
  IS_MAC ? { id: 'meta', Glyph: CommandIcon } : { id: 'meta', label: 'Ctrl' },
  { id: 'enter', Glyph: EnterIcon },
];

export const CLOSE_KEYS: readonly IKey[] = [{ id: 'esc', label: 'Esc' }];

export const Keycap = ({ tone, cap }: { tone: TKeycapTone; cap: IKey }) => (
  <kbd
    aria-hidden
    className={cn(
      'inline-flex h-keycap min-w-keycap shrink-0 items-center justify-center rounded-tag px-tight',
      'font-mono text-meta font-medium',
      TONE[tone],
    )}
  >
    {cap.Glyph ? <cap.Glyph /> : cap.label}
  </kbd>
);

// One plate per chord: a plate per key pushed a footer's buttons into a column.
export const KeyChord = ({ tone, keys }: { tone: TKeycapTone; keys: readonly IKey[] }) => (
  <kbd
    aria-hidden
    className={cn(
      'inline-flex h-keycap min-w-keycap shrink-0 items-center justify-center gap-hair rounded-tag px-tight',
      'font-mono text-meta font-medium',
      TONE[tone],
    )}
  >
    {keys.map((cap) =>
      cap.Glyph ? <cap.Glyph key={cap.id} size={12} /> : <span key={cap.id}>{cap.label}</span>,
    )}
  </kbd>
);

const GLYPH_KEYS: Record<string, IKey> = {
  '⌘': IS_MAC ? { id: 'meta', Glyph: CommandIcon } : { id: 'meta', label: 'Ctrl' },
  '↵': { id: 'enter', Glyph: EnterIcon },
  '⌫': { id: 'backspace', Glyph: BackspaceIcon },
};

export const keysOf = (hint: string): IKey[] =>
  [...hint].map((char, index) => GLYPH_KEYS[char] ?? { id: `${char}-${index}`, label: char });

export const Shortcut = ({
  hint,
  tone = 'menu',
  className,
}: {
  hint: string;
  tone?: TKeycapTone;
  className?: string;
}) => (
  <span className={cn('ml-auto flex shrink-0 items-center gap-hair pl-3', className)}>
    {keysOf(hint).map((cap) => (
      <Keycap key={cap.id} tone={tone} cap={cap} />
    ))}
  </span>
);
